import { LabelImageType, LabelInstance, LabelType } from "@prism/prism-shared";
import { parseLabelId } from "./parse-label-id.function";
import * as QRCode from "qrcode";
import * as pako from 'pako';

const OBSESSED_GARAGE_LABEL_LOGO_URL = '/assets/images/label-logos/obsessed-garage-logo.png';

function sanitizeForZpl(text: string): string {
    return text
        .replace(/[\u201C\u201D\u201E\u201F]/g, '"')  // smart double quotes → ASCII "
        .replace(/[\u2018\u2019\u201A\u201B]/g, "'")   // smart single quotes → ASCII '
        .replace(/\u2033/g, '"')                        // double prime (″) → ASCII "
        .replace(/\u2032/g, "'")                        // single prime (′) → ASCII '
        .replace(/\u2013/g, '-')                        // en dash → hyphen
        .replace(/\u2014/g, '-')                        // em dash → hyphen
        .replace(/\u2026/g, '...')                      // ellipsis → three dots
        .replace(/[^\x20-\x7E]/g, '');                  // strip remaining non-ASCII
}

function canvasToBlob(canvas: HTMLCanvasElement, type = 'image/png'): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
                return;
            }
            reject(new Error('Failed to convert canvas to image'));
        }, type);
    });
}

async function convertWhiteTransparentLogoToBlackOnWhite(blob: Blob): Promise<Blob> {
    const image = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Canvas 2D context not supported');

    ctx.drawImage(image, 0, 0);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let index = 0; index < imageData.data.length; index += 4) {
        const alpha = imageData.data[index + 3];
        const value = 255 - alpha;
        imageData.data[index] = value;
        imageData.data[index + 1] = value;
        imageData.data[index + 2] = value;
        imageData.data[index + 3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);

    return canvasToBlob(canvas);
}

async function rotateImageBlob90CounterClockwise(blob: Blob): Promise<Blob> {
    const image = await createImageBitmap(blob);
    const canvas = document.createElement('canvas');
    canvas.width = image.height;
    canvas.height = image.width;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Canvas 2D context not supported');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.translate(0, image.width);
    ctx.rotate(-Math.PI / 2);
    ctx.drawImage(image, 0, 0);

    return canvasToBlob(canvas);
}

async function loadObsessedGarageLabelLogo(): Promise<Blob> {
    const response = await fetch(OBSESSED_GARAGE_LABEL_LOGO_URL);
    if (!response.ok) {
        throw new Error(`Failed to load Obsessed Garage label logo (${response.status})`);
    }
    return convertWhiteTransparentLogoToBlackOnWhite(await response.blob());
}

function canvasFont(size: number, weight: string = '400'): string {
    return `${weight} ${size}px Arial, Helvetica, sans-serif`;
}

function fitCanvasFontSize(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    maxSize: number,
    minSize: number,
    weight: string = '400',
): number {
    for (let size = maxSize; size >= minSize; size -= 2) {
        ctx.font = canvasFont(size, weight);
        if (ctx.measureText(text).width <= maxWidth) {
            return size;
        }
    }
    return minSize;
}

function wrapCanvasText(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    maxLines: number,
): string[] {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
        const candidate = currentLine ? `${currentLine} ${word}` : word;
        if (ctx.measureText(candidate).width <= maxWidth || !currentLine) {
            currentLine = candidate;
            continue;
        }

        lines.push(currentLine);
        currentLine = word;

        if (lines.length === maxLines) {
            return lines;
        }
    }

    if (currentLine && lines.length < maxLines) {
        lines.push(currentLine);
    }

    return lines;
}

/**
 * Largest font height (down to minSize) at which `text` still fits maxWidth on ONE line
 * of a ZPL ^A0 field. Measures the glyphs rather than guessing from character count — a
 * fixed advance-per-char estimate has to assume worst-case-wide glyphs, so it shrinks
 * text that would have fit. ^A0 is a Helvetica-alike, so Arial at the same pixel size
 * measures close enough to pick the size.
 */
function fitZplFontSize(text: string, maxWidth: number, maxSize: number, minSize: number): number {
    const ctx = document.createElement('canvas').getContext('2d', { willReadFrequently: true });
    if (!ctx || typeof ctx.measureText !== 'function') return minSize;

    // a hair of slack, since the printer's metrics aren't exactly Arial's
    const limit = maxWidth * 0.98;
    for (let size = maxSize; size > minSize; size -= 2) {
        ctx.font = canvasFont(size);
        if (ctx.measureText(text).width <= limit) {
            return size;
        }
    }
    return minSize;
}

function drawC3HardwareOrientationArrow(
    ctx: CanvasRenderingContext2D,
    direction: 'up' | 'down' | 'both',
    x: number,
    y: number,
    width: number,
    height: number,
): void {
    ctx.save();
    ctx.translate(x, y);
    ctx.fillStyle = '#000000';

    const centerX = width / 2;
    const marginX = Math.round(width * 0.06);
    const topMargin = Math.round(height * 0.05);
    const bottomMargin = Math.round(height * 0.05);
    const shaftWidth = Math.round(width * 0.30);
    const shaftLeft = centerX - (shaftWidth / 2);
    const shaftRight = centerX + (shaftWidth / 2);
    const headBaseOffset = Math.round(height * 0.42);

    ctx.beginPath();
    if (direction === 'both') {
        const topHeadBaseY = topMargin + Math.round(height * 0.28);
        const bottomY = height - bottomMargin;
        const bottomHeadBaseY = bottomY - Math.round(height * 0.28);

        ctx.moveTo(centerX, topMargin);
        ctx.lineTo(width - marginX, topHeadBaseY);
        ctx.lineTo(shaftRight, topHeadBaseY);
        ctx.lineTo(shaftRight, bottomHeadBaseY);
        ctx.lineTo(width - marginX, bottomHeadBaseY);
        ctx.lineTo(centerX, bottomY);
        ctx.lineTo(marginX, bottomHeadBaseY);
        ctx.lineTo(shaftLeft, bottomHeadBaseY);
        ctx.lineTo(shaftLeft, topHeadBaseY);
        ctx.lineTo(marginX, topHeadBaseY);
    } else if (direction === 'up') {
        const headBaseY = topMargin + headBaseOffset;
        const bottomY = height - bottomMargin;

        ctx.moveTo(centerX, topMargin);
        ctx.lineTo(width - marginX, headBaseY);
        ctx.lineTo(shaftRight, headBaseY);
        ctx.lineTo(shaftRight, bottomY);
        ctx.lineTo(shaftLeft, bottomY);
        ctx.lineTo(shaftLeft, headBaseY);
        ctx.lineTo(marginX, headBaseY);
    } else {
        const headBaseY = height - bottomMargin - headBaseOffset;
        const topY = topMargin;
        const bottomY = height - bottomMargin;

        ctx.moveTo(shaftLeft, topY);
        ctx.lineTo(shaftRight, topY);
        ctx.lineTo(shaftRight, headBaseY);
        ctx.lineTo(width - marginX, headBaseY);
        ctx.lineTo(centerX, bottomY);
        ctx.lineTo(marginX, headBaseY);
        ctx.lineTo(shaftLeft, headBaseY);
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
}

function rotateCanvasClockwise(sourceCanvas: HTMLCanvasElement): HTMLCanvasElement {
    const rotatedCanvas = document.createElement('canvas');
    rotatedCanvas.width = sourceCanvas.height;
    rotatedCanvas.height = sourceCanvas.width;

    const ctx = rotatedCanvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Canvas 2D context not supported');

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rotatedCanvas.width, rotatedCanvas.height);
    ctx.translate(rotatedCanvas.width, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(sourceCanvas, 0, 0);

    return rotatedCanvas;
}

function createC3HardwareOrientationLabelCanvas(
    partNumber: string,
    vendorPartNumber: string,
    description: string,
    arrowDirection: 'up' | 'down' | 'both',
): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = 1218;
    canvas.height = 812;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) throw new Error('Canvas 2D context not supported');

    const labelX = 55;
    const leftWidth = 785;
    const dividerX = 880;
    const arrowWidth = 260;
    const arrowHeight = 610;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000000';
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';

    // The vendor SKU (e.g. CC4000-00-232) is the primary identifier on the label; the hardware
    // part number is secondary. The SKU is longer than a part number, so it gets a smaller font
    // range. Fall back to the part number as primary when there is no SKU.
    const primaryText = vendorPartNumber || partNumber;
    const secondaryText = vendorPartNumber ? partNumber : '';

    const primaryFont = fitCanvasFontSize(ctx, primaryText, leftWidth, 150, 55, '700');
    ctx.font = canvasFont(primaryFont, '700');
    ctx.fillText(primaryText, labelX, 90);

    ctx.fillRect(labelX, 285, leftWidth, 3);

    const descriptionY = secondaryText ? 410 : 325;
    if (secondaryText) {
        const secondaryFont = fitCanvasFontSize(ctx, secondaryText, leftWidth, 82, 58, '700');
        ctx.font = canvasFont(secondaryFont, '700');
        ctx.fillText(secondaryText, labelX, 315);
    }

    const descriptionFont = Math.max(48, Math.min(74, Math.floor(leftWidth / Math.max(description.length * 0.32, 1))));
    ctx.font = canvasFont(descriptionFont, '400');
    wrapCanvasText(ctx, description, leftWidth, 5)
        .forEach((line, index) => ctx.fillText(line, labelX, descriptionY + (index * (descriptionFont + 16))));

    ctx.fillRect(dividerX, 60, 4, 692);
    drawC3HardwareOrientationArrow(ctx, arrowDirection, 930, 100, arrowWidth, arrowHeight);

    return canvas;
}

export async function generateZplLabelString(
    labelInstance: LabelInstance | null,
    secondary: boolean = false,
): Promise<string> {
    if (!labelInstance) {
        return '';
    }

    if (labelInstance.type === LabelType.PackingSlipBatch) {
        if (labelInstance.labels.length < 1 || labelInstance.labels.length > 4
            || new Set(labelInstance.labels.map(label => label.id)).size !== labelInstance.labels.length) {
            throw new Error('Batch label requires one to four distinct packing-slip labels');
        }
        const safe = (value: string) => sanitizeForZpl(value || '').replace(/[\^~]/g, '');
        const partNumberKey = (value: string) => safe(value).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        const order = labelInstance.labels[0];
        const commonPurchaseOrderNumber = labelInstance.labels.every(label => label.purchaseOrderNumber === order.purchaseOrderNumber)
            ? order.purchaseOrderNumber : '';
        let logoUrl = '/assets/images/label-logos/seljan_logo_plain.png';
        let logoLeftOffset = 220;
        if (labelInstance.imageType === LabelImageType.Kolorcans) {
            logoUrl = '/assets/images/label-logos/kolorcans_logo.png';
            logoLeftOffset = 110;
        } else if (labelInstance.imageType === LabelImageType.Suncast) {
            logoUrl = '/assets/images/label-logos/suncast_logo.jpg';
            logoLeftOffset = 200;
        } else if (labelInstance.imageType === LabelImageType.FHNoble) {
            logoUrl = '/assets/images/label-logos/fhnoble_logo.png';
            logoLeftOffset = 200;
        }
        const logo = await fetch(logoUrl)
            .then(response => response.blob())
            .then(blob => convertBlobToZPL(blob, 50, 100));
        let zpl = '^XA\n^PW812\n^LL1218\n';
        zpl += `^FO${logoLeftOffset},15\n${logo}^FS\n`;
        zpl += '^FO0,130^GB812,4,4^FS\n';
        zpl += `^FO20,165^A0N,40,36^FB770,1,0,L,0^FD${safe(order.customerName)}^FS\n`;
        zpl += `^FO20,235^A0N,32,30^FB370,1,0,L,0^FDSO: ${safe(order.salesOrderNumber || '------')}^FS\n`;
        zpl += `^FO410,235^A0N,32,30^FB382,1,0,L,0^FDPO: ${safe(commonPurchaseOrderNumber || '------')}^FS\n`;
        zpl += '^FO0,300^GB812,4,4^FS\n';

        labelInstance.labels.forEach((label, index) => {
            const cardWidth = 390;
            const cardHeight = 400;
            const x = 10 + (index % 2) * 400;
            const y = 315 + Math.floor(index / 2) * 410;
            const shortLabelId = safe(parseLabelId(label.id));
            zpl += `^FO${x},${y}^GB${cardWidth},${cardHeight},2^FS\n`;
            const hasSeparatePartNumber = !!label.customerPartNumber && !!label.seljanPartNumber
                && partNumberKey(label.seljanPartNumber) !== partNumberKey(label.customerPartNumber);
            if (labelInstance.layout === 'internal') {
                zpl += `^FO${x + 15},${y + 12}^A0N,32,29^FB360,2,0,L,0^FD${safe(label.customerPartNumber || label.seljanPartNumber)}^FS\n`;
                zpl += `^FO${x + 15},${y + 82}^A0N,26,24^FB360,3,2,L,0^FD${safe(label.description)}^FS\n`;
                if (hasSeparatePartNumber) {
                    zpl += `^FO${x + 255},${y + 238}^A0N,18,16^FB120,1,0,C,0^FD${safe(label.seljanPartNumber)}^FS\n`;
                }
                zpl += `^FO${x + 15},${y + 165}^BQN,2,8,H^FDLA,${safe(label.id)}^FS\n`;
                zpl += `^FO${x + 255},${y + 165}^A0N,22,20^FB120,1,0,C,0^FDCODE^FS\n`;
                zpl += `^FO${x + 255},${y + 190}^A0N,38,34^FB120,1,0,C,0^FD${shortLabelId}^FS\n`;
                zpl += `^FO${x + 255},${y + 272}^A0N,36,31^FB110,1,0,C,0^FDQTY^FS\n`;
                zpl += `^FO${x + 255},${y + 312}^A0N,82,${Math.min(70, Math.floor(125 / String(label.quantity).length))}^FB120,1,0,C,0^FD${label.quantity}^FS\n`;
                return;
            }
            const scannedBoxSize = 128;
            const qrX = x + 15;
            const qrY = y + 210;
            const scannedBoxX = x + cardWidth - scannedBoxSize;
            const scannedBoxY = y + cardHeight - scannedBoxSize;
            zpl += `^FO${x + 15},${y + 12}^A0N,32,29^FB245,2,0,L,0^FD${safe(label.customerPartNumber || label.seljanPartNumber)}^FS\n`;
            zpl += `^FO${x + 15},${y + 82}^A0N,21,19^FB245,2,2,L,0^FD${safe(label.description)}^FS\n`;
            if (hasSeparatePartNumber) {
                zpl += `^FO${x + 15},${y + 130}^A0N,24,22^FB245,1,0,L,0^FD${safe(label.seljanPartNumber)}^FS\n`;
            }
            zpl += `^FO${qrX},${qrY}^BQN,2,7,H^FDLA,${safe(label.id)}^FS\n`;
            zpl += `^FO${x + 255},${y + 165}^A0N,22,20^FB120,1,0,C,0^FDCODE^FS\n`;
            zpl += `^FO${x + 255},${y + 190}^A0N,38,34^FB120,1,0,C,0^FD${shortLabelId}^FS\n`;
            zpl += `^FO${x + 292},${y + 15}^A0N,38,31^FB88,1,0,C,0^FDQTY^FS\n`;
            zpl += `^FO${x + 292},${y + 52}^A0N,78,${Math.min(64, Math.floor(110 / String(label.quantity).length))}^FB88,1,0,C,0^FD${label.quantity}^FS\n`;
            // The card's right and bottom border complete the scanned checkbox.
            zpl += `^FO${scannedBoxX},${scannedBoxY - 28}^A0N,22,18^FB${scannedBoxSize},1,0,C,0^FDSCANNED^FS\n`;
            zpl += `^FO${scannedBoxX},${scannedBoxY}^GB${scannedBoxSize},2,2^FS\n`;
            zpl += `^FO${scannedBoxX},${scannedBoxY}^GB2,${scannedBoxSize},2^FS\n`;
        });
        return `${zpl}^XZ`;
    }

    if (labelInstance.type === LabelType.PackingSlip) {
        const smallGap = 50;
        const gap = 75;
        const labelX = 20;
        const textFont = '^A0N,40,40';
        const lineWidth = 900;


        let zpl = '^XA\n';
        let y = 20
        if (!secondary) {
            let imageLeftOffset = 150;
            let imageZPL!: Promise<string>
            switch (labelInstance.imageType) {
                case LabelImageType.Seljan:
                    imageLeftOffset = 240;
                    imageZPL = fetch('/assets/images/label-logos/seljan_logo_plain.png').then((response) => {
                        return response.blob();
                    }).then((blob) => {
                        return convertBlobToZPL(blob, 50, 100);
                    })
                    break;
                case LabelImageType.Kolorcans:
                    imageLeftOffset = 130;
                    imageZPL = fetch('/assets/images/label-logos/kolorcans_logo.png').then((response) => {
                        return response.blob();
                    }).then((blob) => {
                        return convertBlobToZPL(blob, 50, 100);
                    })
                    break;
                case LabelImageType.Suncast:
                    imageLeftOffset = 220;
                    imageZPL = fetch('/assets/images/label-logos/suncast_logo.jpg').then((response) => {
                        return response.blob();
                    }).then((blob) => {
                        return convertBlobToZPL(blob, 50, 100);
                    })
                    break;
                case LabelImageType.FHNoble:
                    imageLeftOffset = 220;
                    imageZPL = fetch('/assets/images/label-logos/fhnoble_logo.png').then((response) => {
                        return response.blob();
                    }).then((blob) => {
                        return convertBlobToZPL(blob, 50, 100);
                    })
                    break;
            }
            const rotateImageBlob90CCW = async (blob: Blob): Promise<Blob> => {
                const img = await createImageBitmap(blob);

                const canvas = document.createElement("canvas");

                canvas.width = img.height;
                canvas.height = img.width;

                const ctx = canvas.getContext("2d", { willReadFrequently: true })!;

                ctx.translate(0, img.width);
                ctx.rotate(-Math.PI / 2);

                ctx.drawImage(img, 0, 0);

                return new Promise<Blob>(resolve => {
                    canvas.toBlob(rotatedBlob => {
                        resolve(rotatedBlob!);
                    }, blob.type);
                });
            }

            // =================              top image         ===================================
            await imageZPL.then((image) => {
                zpl += `^FO${imageLeftOffset},${y}\n${image}^FS\n`;
                y += 120
                zpl += `^FO0,${y}^GB${lineWidth},4,4^FS\n`;

                y += 30;
            })

            // ==================         customer           ==================================
            zpl += `^FO${labelX},${y}\n^A0N,40,30\n^FD${labelInstance.customerName}^FS\n`;
            y += smallGap;

            // ===================      customer part number      =================================

            zpl += `^FO${labelX},${y}\n^A0N,70,70\n^FD${labelInstance.customerPartNumber}^FS\n`;
            y += gap;

            // =================           seljan part number       ===================================

            zpl += `^FO${labelX},${y}\n${textFont}\n^FD${labelInstance.seljanPartNumber}^FS\n`;
            y += smallGap;

            // ==================         description           ==================================
            zpl += `^FO${labelX},${y}\n^A0N,40,30\n^FD${labelInstance.description}^FS\n`;

            y += smallGap;
            zpl += `^FO0,${y}^GB${lineWidth},2,2^FS\n`;
            y += 30;
            // ==============        PO number         ======================================
            zpl += `^FO${labelX},${y}\n${textFont}\n^FDPO:^FS\n`;
            zpl += `^FO100,${y}\n${textFont}\n^FD${labelInstance.purchaseOrderNumber || '------'}^FS\n`;

            y += smallGap;

            // ==============            SO number         ======================================
            zpl += `^FO${labelX},${y}\n${textFont}\n^FDS/O:^FS\n`;
            zpl += `^FO100,${y}\n${textFont}\n^FD${labelInstance.salesOrderNumber || '------'}^FS\n`;

            y += smallGap;
            zpl += `^FO0,${y}^GB${lineWidth},4,4^FS\n`;
            zpl += `^FO400,${y}^GB4,800,4^FS\n`;
            y += 30;

            // =============            quantity         =======================================
            zpl += `^FO535,560\n^A0N,63,63\n^FDQTY:^FS\n`;
            zpl += `^FO420,640\n^A0N,95,95\n^FB360,1,0,C,0\n^FD${labelInstance.quantity}^FS\n`;
            zpl += `^FO400,740^GB${lineWidth},2,2^FS\n`;

            // ====================        QR code        ===================================
            zpl += `^FO475,780\n^A0N,63,63\n^FB250,1,0,C,0\n^FD${parseLabelId(labelInstance.id)}^FS\n`
            zpl += `^FO475,850\n^BQN,2,10,H\n^FDLA,${labelInstance.id}^FS\n`;

            if (!labelInstance.qualityAlertIds || !labelInstance.qualityAlertIds.length) {
                let imageUrl: string = '/assets/images/label-logos/seljan_logo_plain.png';
                if (labelInstance.imageType === LabelImageType.Kolorcans) {
                    imageUrl = '/assets/images/label-logos/kolorcans_logo.png';
                } else if (labelInstance.imageType === LabelImageType.Suncast) {
                    imageUrl = '/assets/images/label-logos/suncast_logo.jpg';
                } else if (labelInstance.imageType === LabelImageType.FHNoble) {
                    imageUrl = '/assets/images/label-logos/fhnoble_logo.png';
                }
                const logoBlob = await fetch(imageUrl).then((response) => {
                    return response.blob();
                })

                const rotatedZpl = await rotateImageBlob90CCW(logoBlob).then(blob => {
                    return convertBlobToZPL(blob, 50, 600);
                });

                let logoX = 110;
                let logoY = 550;

                if (labelInstance.imageType === LabelImageType.Kolorcans) {
                    logoX = 150;
                    logoY = 570;
                } else if (labelInstance.imageType === LabelImageType.Suncast) {
                    logoX = 115;
                    logoY = 570;
                } else if (labelInstance.imageType === LabelImageType.FHNoble) {
                    logoX = 130;
                    logoY = 570;
                }

                zpl += `^FO${logoX},${logoY}\n${rotatedZpl}^FS\n`;
            } else {
                const boxSize = 100;

                zpl += `^FO${labelX + 20},${y}^A0N,70,70^FDInspections^FS`;
                y += 85;
                labelInstance.qualityAlertIds.forEach(id => {

                    zpl += `^FO${labelX},${y}^GB${boxSize},${boxSize},2^FS`;
                    zpl += `^FO${labelX + boxSize + 20},${y + 15}^A0N,80,90^FD${id.slice(-4)}^FS`;
                    y += 120;
                });
            }

            //===============      print quantity      ======================================
            // zpl += `^PQ${labelInstance.printQuantity}\n^XZ`;
            zpl += `^XZ`;
            return zpl;

        } else {
            zpl += `^FO600,${y}^A0N,45,35^FDScanned^FS`;
            zpl += `^FO${labelX},${y}\n^A0N,40,30\n^FD${labelInstance.customerName}^FS\n`;
            y += smallGap
            zpl += `^FO${labelX},${y}\n^A0N,70,60\n^FD${labelInstance.seljanPartNumber}^FS\n`;
            zpl += `^FO570,${y}^GB175,175,2^FS`;
            y += gap;
            zpl += `^FO${labelX},${y}^A0N,40,30^FB500,5,0,L,0^FD${labelInstance.description}^FS`;

            y += 125;
            zpl += `^FO0,${y}^GB${lineWidth},2,2^FS\n`;
            y += 30;

            const leftColX = labelX;
            const rightColX = labelX + 250;

            zpl += `^FO${leftColX},${y}\n^A0N,40,40\n^FDS/O:^FS\n`;
            zpl += `^FO${rightColX},${y}\n^A0N,40,40\n^FDPO:^FS\n`;

            y += smallGap;

            zpl += `^FO${leftColX},${y}\n^A0N,65,55\n^FD${labelInstance.salesOrderNumber ? labelInstance.salesOrderNumber : '------'}^FS\n`;
            zpl += `^FO${rightColX},${y}\n^A0N,65,55\n^FD${labelInstance.purchaseOrderNumber ? labelInstance.purchaseOrderNumber : '------'}^FS\n`;

            y += gap;
            zpl += `^FO0,${y}^GB${lineWidth},2,2^FS\n`;
            y += 30;
            const qrBlob = await generateQrBlob(labelInstance.id);
            const qrZpl = await convertBlobToZPL(qrBlob, 50, 700);
            zpl += `^FO50,${y - 40}\n${qrZpl}^FS\n`;

            const dateStr = labelInstance.printDate.toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            })
            zpl += `^FO730,${y + 220}\n^A0B,70,50\n^FD${dateStr}^FS\n`;

            y += 650
            const x = 85;
            zpl += `^FO${x},${y}\n^A0N,90,75\n^FD${parseLabelId(labelInstance.id)}^FS\n`;
            zpl += `^FO${x + 420},${y}\n^A0N,90,75\n^FDQTY: ${labelInstance.quantity}^FS\n`;

            zpl += `^XZ`;
            return zpl;
        }
    }

    if (labelInstance.type === LabelType.ObsessedEngineeringSkid) {
        const labelWidth = 812;
        const labelHeight = 1218;
        const labelX = 20;
        const lineWidth = labelWidth;
        const orderNumber = sanitizeForZpl(labelInstance.orderNumber || '------');
        const customerPurchaseOrderNumber = sanitizeForZpl(labelInstance.customerPurchaseOrderNumber || '------');
        const labelId = sanitizeForZpl(labelInstance.id);
        const shortLabelId = sanitizeForZpl(parseLabelId(labelInstance.id));

        let zpl = `^XA\n^PW${labelWidth}\n^LL${labelHeight}\n^CI28\n`;
        let y = 20;

        const logoBlob = await loadObsessedGarageLabelLogo();
        const topLogoZpl = await convertBlobToZPL(logoBlob, 50, 120);
        const topLogoWidth = Math.round((3055 / 1190) * 120);
        const topLogoX = Math.round((labelWidth - topLogoWidth) / 2);

        zpl += `^FO${topLogoX},${y}\n${topLogoZpl}^FS\n`;
        y += 145;
        zpl += `^FO0,${y}^GB${lineWidth},4,4^FS\n`;
        y += 35;

        const leftColumnX = labelX;
        const rightColumnX = 420;
        zpl += `^FO${leftColumnX},${y}^A0N,35,35^FDORDER^FS\n`;
        zpl += `^FO${rightColumnX},${y}^A0N,35,35^FDCUSTOMER PO^FS\n`;
        y += 50;
        zpl += `^FO${leftColumnX},${y}^A0N,65,55^FB370,1,0,L,0^FD${orderNumber}^FS\n`;
        zpl += `^FO${rightColumnX},${y}^A0N,65,55^FB370,1,0,L,0^FD${customerPurchaseOrderNumber}^FS\n`;
        y += 90;
        zpl += `^FO0,${y}^GB${lineWidth},4,4^FS\n`;
        zpl += `^FO400,${y}^GB4,${labelHeight - y},4^FS\n`;

        const rotatedLogoBlob = await rotateImageBlob90CounterClockwise(logoBlob);
        const rotatedLogoZpl = await convertBlobToZPL(rotatedLogoBlob, 50, 650);
        zpl += `^FO85,${y + 80}\n${rotatedLogoZpl}^FS\n`;

        zpl += `^FO475,${y + 290}^A0N,63,63^FB250,1,0,C,0^FD${shortLabelId}^FS\n`;
        zpl += `^FO475,${y + 365}^BQN,2,10,H^FDLA,${labelId}^FS\n`;
        zpl += '^XZ';
        return zpl;
    }

    if (labelInstance.type === LabelType.AsMolded) {
        const [base, ...rest] = labelInstance.partNumber.split('-');
        const colorCode = rest.join('-');
        const description = sanitizeForZpl(labelInstance.description || '');
        const printDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });

        let zpl = '^XA\n^PW812\n^LL1218\n'

        // base part number (centered)
        zpl += `^FO0,60\n^A0N,200,200\n^FB812,1,0,C,0\n^FD${base.toUpperCase()}^FS\n`

        // color code (centered, very large)
        let nextY = 270;
        if (colorCode) {
            zpl += `^FO0,${nextY}\n^A0N,260,260\n^FB812,1,0,C,0\n^FD${colorCode}^FS\n`
            nextY = 500;
        }

        // horizontal rule
        zpl += `^FO20,${nextY}\n^GB772,0,2^FS\n`

        // description (wrapped)
        zpl += `^FO20,${nextY + 12}\n^A0N,32,32\n^FB770,2,0,L,0\n^FD${description}^FS\n`

        // print date
        zpl += `^FO20,${nextY + 50}\n^A0N,28,28\n^FD${printDate}^FS\n`

        // QR code with productId (image-based, same approach as packing slip)
        const qrY = nextY + 90;
        const remainingHeight = 1218 - qrY - 20;
        const qrBlob = await generateQrBlob(labelInstance.productId, 600);
        const qrZpl = await convertBlobToZPL(qrBlob, 50, remainingHeight);
        const qrX = Math.floor((812 - remainingHeight) / 2);
        zpl += `^FO${qrX},${qrY}\n${qrZpl}^FS\n`

        zpl += `^XZ`

        return zpl
    }

    if (labelInstance.type === LabelType.CornerLabel) {
        const partNumber = labelInstance.partNumber;
        const description = labelInstance.description;
        const boxString = `BOX ${labelInstance.boxIndex + 1} OF ${labelInstance.boxCount}`;
        const poString = `PO: ${labelInstance.purchaseOrderNumber}`;
        const productCountString = `PRODUCTS - ${labelInstance.productCount}`


        let zpl = '^XA\n';
        zpl += '^PR3\n';
        zpl += '^FO0,609^GB812,5,5^FS\n';

        /**
         * BOX NUMBER START
         */
        zpl += '^FO0,30\n';
        zpl += '^A0R,25,25\n';
        zpl += '^FB130,2,0,L,0\n';
        zpl += `^FD${boxString}^FS\n`

        zpl += '^FO0,689\n';
        zpl += '^A0R,25,25\n';
        zpl += '^FB130,2,0,L,0\n';
        zpl += `^FD${boxString}^FS\n`
        /**
         * BOX NUMBER END
         */

        /**
         * PURCHASE ORDER NUMBER START
         */
        zpl += '^FO20,30\n';
        zpl += '^A0R,45,45\n';
        zpl += '^FB530,2,0,L,0\n';
        zpl += `^FD${poString}^FS\n`

        zpl += '^FO20,689\n';
        zpl += '^A0R,45,45\n';
        zpl += '^FB530,2,0,L,0\n';
        zpl += `^FD${poString}^FS\n`
        /**
         * PURCHASE ORDER NUMBER END
         */

        /**
         * PRODUCT COUNT START
         */
        zpl += '^FO0,400\n';
        zpl += '^A0R,25,25\n';
        zpl += '^FB300,2,0,L,0\n';
        zpl += `^FD${productCountString}^FS\n`

        zpl += '^FO0,1009\n';
        zpl += '^A0R,25,25\n';
        zpl += '^FB300,2,0,L,0\n';
        zpl += `^FD${productCountString}^FS\n`
        /**
         * PRODUCT COUNT END
         */

        /**
         * LEFT CORNER SECTION START
         */
        const left_y: number = 50;
        zpl += `^FO350,${left_y}\n`;
        zpl += '^A0R,73,73\n';
        zpl += '^FB500,5,0,L,0\n';
        zpl += `^FD${partNumber}^FS\n`;

        zpl += `^FO475,${left_y}\n`;
        zpl += '^A0R,30,30\n';
        zpl += '^FB459,5,0,L,0\n';
        zpl += `^FD${description}^FS\n`;

        if (labelInstance.boxCount > 1) {
            let x = 375
            for (const data of labelInstance.lineItemData) {
                const partNumberString = `${labelInstance.productCount}x - ${data.partNumber}`;
                zpl += `^FO${x},${left_y}\n`;
                zpl += '^A0R,30,30\n';
                zpl += '^FB459,5,0,L,0\n';
                zpl += `^FD${partNumberString}^FS\n`;

                zpl += `^FO${x + 14},${left_y + 10}\n`;
                zpl += '^A0R,20,20\n';
                zpl += '^FB559,5,0,L,0\n';
                zpl += `^FD${data.description}^FS\n`;

                x -= 80;
            }
        }
        /**
         * LEFT CORNER SECTION END
         */

        /**
         * RIGHT CORNER SECTION START
         */
        const right_y: number = 659;
        zpl += `^FO350,${right_y}\n`;
        zpl += '^A0R,73,73\n';
        zpl += '^FB500,5,0,L,0\n';
        zpl += `^FD${partNumber}^FS\n`;

        zpl += `^FO475,${right_y}\n`;
        zpl += '^A0R,30,30\n';
        zpl += '^FB459,5,0,L,0\n';
        zpl += `^FD${description}^FS\n`;

        if (labelInstance.boxCount > 1) {
            let x = 375
            for (const data of labelInstance.lineItemData) {
                const partNumberString = `${labelInstance.productCount}x - ${data.partNumber}`;
                zpl += `^FO${x},${right_y}\n`;
                zpl += '^A0R,30,30\n';
                zpl += '^FB459,5,0,L,0\n';
                zpl += `^FD${partNumberString}^FS\n`;

                zpl += `^FO${x + 14},${right_y + 10}\n`;
                zpl += '^A0R,20,20\n';
                zpl += '^FB559,5,0,L,0\n';
                zpl += `^FD${data.description}^FS\n`;

                x -= 80;
            }
        }
        /**
         * RIGHT CORNER SECTION END
         */

        zpl += `^PQ${labelInstance.printQuantity}\n`;

        zpl += '^XZ';

        return zpl;
    }

    if (labelInstance.type === LabelType.FHCornerLabel) {
        const salesOrderNumber = sanitizeForZpl(labelInstance.salesOrderNumber || '').replace(/[\^~]/g, '').trim();
        let zpl = '';
        zpl += '^XA\n^MMT\n^PW812\n^LL1218\n^LS0\n';
        zpl += '^FO3,606^GB803,0,8^FS\n';
        zpl += '^FT88,537^A0B,80,103^FH\^FDF.H NOBLE^FS\n';
        zpl += '^FT88,1142^A0B,80,103^FH\^FDF.H NOBLE^FS\n';
        zpl += '^FT117,296^A0B,17,24^FH\^FDLAKE MILLS,WI 53551^FS\n';
        zpl += '^FT117,902^A0B,17,24^FH\^FDLAKE MILLS,WI 53551^FS\n';

        if (salesOrderNumber) {
            zpl += `^FT153,558^A0B,40,42^FDSO: ${salesOrderNumber}^FS\n`;
            zpl += `^FT154,558^A0B,40,42^FDSO: ${salesOrderNumber}^FS\n`;
            zpl += `^FT153,1164^A0B,40,42^FDSO: ${salesOrderNumber}^FS\n`;
            zpl += `^FT154,1164^A0B,40,42^FDSO: ${salesOrderNumber}^FS\n`;
        }

        zpl += `^FT237,600^A0B,75,64^FB600,1,0,C,0^FH\^FD${labelInstance.parsedDesign || 'UNKNOWN'} URN^FS\n`;
        zpl += `^FT237,1200^A0B,75,64^FB600,1,0,C,0^FH\^FD${labelInstance.parsedDesign || 'UNKNOWN'} URN^FS\n`;

        const posDict: Record<number, number> = {
            1: 590,
            2: 560,
            3: 520,
            4: 440,
        }

        const gapDict: Record<number, number> = {
            1: 150,
            2: 137,
            3: 125,
            4: 118,
        }

        let pos = posDict[labelInstance.addedHardwareData.length] || 440;
        const gap = gapDict[labelInstance.addedHardwareData.length] || 100;

        const productTextWidth = labelInstance.productCount > 9 ? '75' : '150'
        zpl += `^FT${pos - gap},520^A0B,71,67^FH\^FD${labelInstance.parsedColor || 'UNKNOWN'}^FS\n`;
        zpl += `^FT${pos - gap},600^A0B,71,${productTextWidth}^FH\^FD${labelInstance.productCount}^FS\n`;
        zpl += `^FT${pos - gap},1120^A0B,71,67^FH\^FD${labelInstance.parsedColor || 'UNKNOWN'}^FS\n`;
        zpl += `^FT${pos - gap},1200^A0B,70,${productTextWidth}^FH\^FD${labelInstance.productCount}^FS\n`;

        for (const item of labelInstance.addedHardwareData) {
            const count = item.count * labelInstance.productCount;
            const textWidth = count > 9 ? '75' : '150'
            const descFontHeight = item.description.length > 30 ? '50' : '80';
            const descFontWidth = item.description.length > 30 ? '24' : '31';

            zpl += `^FT${pos},520^A0B,${descFontHeight},${descFontWidth}^FH\^FD${item.description}^FS\n`;
            zpl += `^FT${pos},600^A0B,80,${textWidth}^FH\^FD${count}^FS\n`;
            zpl += `^FT${pos},1120^A0B,${descFontHeight},${descFontWidth}^FH\^FD${item.description}^FS\n`;
            zpl += `^FT${pos},1200^A0B,80,${textWidth}^FH\^FD${count}^FS\n`;

            pos += gap;
        }

        zpl += `^PQ${labelInstance.printQuantity}\n`;
        zpl += '^XZ';

        return zpl
    }

    if (labelInstance.type === LabelType.PowderCoating) {

        // Load and convert logo image
        const imageLeftOffset = 240;
        const imageZPL = await fetch('/assets/images/label-logos/seljan_logo_plain.png')
            .then(response => response.blob())
            .then(blob => convertBlobToZPL(blob, 50, 100));

        // Layout constants
        const splitGap = 65; // Gap for split labels
        const fullSizeGap = 80; // Gap for full-size labels
        const borderInset = 15; // Distance from edge to border
        const borderThickness = 8;
        const padding = 20; // Additional padding inside border
        const labelWidth = 812;
        const labelHeight = 1218;
        const centerY = 609; // Center line for split
        const logoLineOffset = 100;

        let zpl = '^XA\n';

        if (labelInstance.split) {
            const splitFontSize = 50;
            const splitDescriptionFontSize = 35; // Smaller font for description
            const sectionHeight = centerY; // Height of each half
            const innerWidth = labelWidth - (2 * borderInset);
            const innerSectionHeight = sectionHeight - (2 * borderInset);

            // Quantity box dimensions
            const qtyBoxWidth = 280;
            const qtyBoxHeight = 200;

            // Helper function to draw border for a section
            const drawSectionBorder = (startY: number): string => {
                let borderZpl = '';
                // Top border
                borderZpl += `^FO${borderInset},${startY + borderInset}^GB${innerWidth},${borderThickness},${borderThickness},B^FS\n`;
                // Bottom border
                borderZpl += `^FO${borderInset},${startY + borderInset + innerSectionHeight - borderThickness}^GB${innerWidth},${borderThickness},${borderThickness},B^FS\n`;
                // Left border
                borderZpl += `^FO${borderInset},${startY + borderInset}^GB${borderThickness},${innerSectionHeight},${borderThickness},B^FS\n`;
                // Right border
                borderZpl += `^FO${borderInset + innerWidth - borderThickness},${startY + borderInset}^GB${borderThickness},${innerSectionHeight},${borderThickness},B^FS\n`;
                return borderZpl;
            };

            // Helper function to generate ZPL for one section
            const generateSectionZpl = (startY: number): string => {
                let sectionZpl = '';
                const labelX = borderInset + padding;
                let sectionY = startY + borderInset + padding;

                // Calculate max width for text (from labelX to right border minus padding)
                const textMaxWidth = (borderInset + innerWidth) - labelX - padding;

                // Add image
                sectionZpl += `^FO${imageLeftOffset},${sectionY}\n${imageZPL}^FS\n`;
                sectionY += logoLineOffset;

                // Line under image (touches borders)
                sectionZpl += `^FO${borderInset},${sectionY}^GB${innerWidth},4,4^FS\n`;
                sectionY += 30;

                // Customer name
                sectionZpl += `^FO${labelX},${sectionY}\n^A0N,${splitFontSize},${splitFontSize}\n^FD${labelInstance.customerName}^FS\n`;
                sectionY += splitGap;

                // Part number
                sectionZpl += `^FO${labelX},${sectionY}\n^A0N,${splitFontSize},${splitFontSize}\n^FD${labelInstance.partNumber}^FS\n`;
                sectionY += splitGap;

                // Description (smaller font with wrapping)
                sectionZpl += `^FO${labelX},${sectionY}\n^A0N,${splitDescriptionFontSize},${splitDescriptionFontSize}\n^FB${textMaxWidth},3,0,L,0\n^FD${labelInstance.description || ''}^FS\n`;

                // PO number (only if present) - anchored to bottom
                if (labelInstance.purchaseOrderNumber) {
                    const poY = startY + borderInset + innerSectionHeight - padding - 40;
                    sectionZpl += `^FO${labelX},${poY}\n^A0N,${splitFontSize},${splitFontSize}\n^FDPO#: ${labelInstance.purchaseOrderNumber}^FS\n`;
                }

                // Quantity box in bottom right corner
                const qtyBoxX = borderInset + innerWidth - qtyBoxWidth;
                const qtyBoxY = startY + borderInset + innerSectionHeight - qtyBoxHeight;

                // Draw box border
                sectionZpl += `^FO${qtyBoxX},${qtyBoxY}^GB${qtyBoxWidth},${qtyBoxHeight},${borderThickness}^FS\n`;

                const qtyTextX = qtyBoxX + borderThickness + 10;
                const qtyTextWidth = qtyBoxWidth - (2 * borderThickness);

                // "QTY:" label (small, on top)
                const qtyLabelY = qtyBoxY + 30;
                sectionZpl += `^FO${qtyTextX - 10},${qtyLabelY}\n^A0N,40,40\n^FB${qtyTextWidth},1,0,C,0\n^FDQTY:^FS\n`;

                // Quantity value (large, centered below label)
                const qtyTextY = qtyBoxY + 80;
                sectionZpl += `^FO${qtyTextX},${qtyTextY}\n^A0N,100,100\n^FB${qtyTextWidth},1,0,C,0\n^FD${labelInstance.quantity}^FS\n`;

                return sectionZpl;
            };

            // ========== TOP HALF ==========
            zpl += drawSectionBorder(0);
            zpl += generateSectionZpl(0);

            // ========== BOTTOM HALF ==========
            zpl += drawSectionBorder(centerY);
            zpl += generateSectionZpl(centerY);

            zpl += `^XZ`;
            return zpl;

        } else {
            const fontSize = 60;
            const descriptionFontSize = 45; // Smaller font for description
            const innerWidth = labelWidth - (2 * borderInset);
            const innerHeight = labelHeight - (2 * borderInset);

            // Quantity box dimensions
            const qtyBoxWidth = 350;
            const qtyBoxHeight = 280;

            // Draw black border frame (inset from edges)
            // Top border
            zpl += `^FO${borderInset},${borderInset}^GB${innerWidth},${borderThickness},${borderThickness},B^FS\n`;
            // Bottom border
            zpl += `^FO${borderInset},${borderInset + innerHeight - borderThickness}^GB${innerWidth},${borderThickness},${borderThickness},B^FS\n`;
            // Left border
            zpl += `^FO${borderInset},${borderInset}^GB${borderThickness},${innerHeight},${borderThickness},B^FS\n`;
            // Right border
            zpl += `^FO${borderInset + innerWidth - borderThickness},${borderInset}^GB${borderThickness},${innerHeight},${borderThickness},B^FS\n`;

            // Adjusted layout with padding
            const labelX = borderInset + padding;
            const lineWidth = innerWidth; // Lines extend to touch the border
            let y = borderInset + padding;

            // Calculate max width for text (from labelX to right border minus padding)
            const textMaxWidth = (borderInset + innerWidth) - labelX - padding;

            // Image and top line
            zpl += `^FO${imageLeftOffset},${y}\n${imageZPL}^FS\n`;
            y += logoLineOffset;
            zpl += `^FO${borderInset},${y}^GB${lineWidth},4,4^FS\n`; // Line touches border
            y += 30;

            // Customer name
            zpl += `^FO${labelX},${y}\n^A0N,${fontSize},${fontSize}\n^FD${labelInstance.customerName}^FS\n`;
            y += fullSizeGap;

            // Part number
            zpl += `^FO${labelX},${y}\n^A0N,${fontSize},${fontSize}\n^FD${labelInstance.partNumber}^FS\n`;
            y += fullSizeGap;

            // PO number (only if present)
            if (labelInstance.purchaseOrderNumber) {
                zpl += `^FO${labelX},${y}\n^A0N,${fontSize},${fontSize}\n^FDPO#: ${labelInstance.purchaseOrderNumber}^FS\n`;
                y += fullSizeGap;
            }

            // Description (smaller font with wrapping)
            zpl += `^FO${labelX},${y}\n^A0N,${descriptionFontSize},${descriptionFontSize}\n^FB${textMaxWidth},4,0,L,0\n^FD${labelInstance.description || ''}^FS\n`;

            // Quantity box in bottom right corner
            const qtyBoxX = borderInset + innerWidth - qtyBoxWidth;
            const qtyBoxY = borderInset + innerHeight - qtyBoxHeight;

            // Draw box border
            zpl += `^FO${qtyBoxX},${qtyBoxY}^GB${qtyBoxWidth},${qtyBoxHeight},${borderThickness}^FS\n`;

            const qtyTextX = qtyBoxX + borderThickness;
            const qtyTextWidth = qtyBoxWidth - (2 * borderThickness);

            // "QTY:" label (small, on top)
            const qtyLabelY = qtyBoxY + 50;
            zpl += `^FO${qtyTextX},${qtyLabelY}\n^A0N,50,50\n^FB${qtyTextWidth},1,0,C,0\n^FDQTY:^FS\n`;

            // Quantity value (large, centered below label)
            const qtyTextY = qtyBoxY + 120;
            zpl += `^FO${qtyTextX},${qtyTextY}\n^A0N,140,140\n^FB${qtyTextWidth},1,0,C,0\n^FD${labelInstance.quantity}^FS\n`;

            zpl += `^XZ`;
            return zpl;
        }
    }

    if (labelInstance.type === LabelType.MetalsProject) {

        const smallGap = 50;
        const gap = 75;
        const labelX = 20;
        const textFont = '^A0N,40,40';
        const lineWidth = 900;

        let zpl = '^XA\n';
        let y = 20;

        // ==================         customer           ==================================
        zpl += `^FO${labelX},${y}\n^A0N,40,30\n^FD${labelInstance.customerName}^FS\n`;
        y += smallGap;

        // ===================      project name      =================================
        zpl += `^FO${labelX},${y}\n^A0N,70,70\n^FD${labelInstance.projectName}^FS\n`;
        y += gap;

        // =================           label description       ===================================
        zpl += `^FO${labelX},${y}\n${textFont}\n^FD${labelInstance.projectDescription}^FS\n`;
        y += smallGap;

        // ==============        Owner         ======================================
        zpl += `^FO${labelX},${y}\n${textFont}\n^FDOwner: ${labelInstance.projectCreatorName}^FS\n`;
        y += smallGap;

        // ==============        Printed date/time         ======================================
        const printDate = labelInstance.printDate;
        const dateStr = printDate.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
        });
        const timeStr = printDate.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
        zpl += `^FO${labelX},${y}\n${textFont}\n^FDPrinted: ${dateStr} ${timeStr}^FS\n`;
        y += smallGap;

        zpl += `^FO0,${y}^GB${lineWidth},2,2^FS\n`;
        y += 40;

        // ==============        PO number         ======================================
        zpl += `^FO${labelX},${y}\n${textFont}\n^FDPO:^FS\n`;
        zpl += `^FO100,${y}\n${textFont}\n^FD${labelInstance.purchaseOrderNumber || '------'}^FS\n`;
        y += smallGap;

        // ==============            SO number         ======================================
        zpl += `^FO${labelX},${y}\n${textFont}\n^FDSO:^FS\n`;
        zpl += `^FO100,${y}\n${textFont}\n^FD${labelInstance.salesOrderNumber || '------'}^FS\n`;

        y += gap;
        // ==============        horizontal line below PO/SO         ======================================
        const middleY = y;
        zpl += `^FO0,${y}^GB${lineWidth},2,2^FS\n`;

        // ==============        vertical line down the center         ======================================
        const centerX = 400;
        const bottomY = 1000;
        const verticalLineHeight = bottomY - y;
        zpl += `^FO${centerX},${y}^GB2,${verticalLineHeight},2^FS\n`;

        // ==============        QR code centered in right section (native ZPL)         ======================================
        const qrCenterY = middleY + (bottomY - middleY) / 2;
        const qrX = 480; // Adjusted left
        const qrY = qrCenterY - 130; // Adjusted up
        zpl += `^FO${qrX},${qrY}\n^BQN,2,10,H\n^FDLA,${labelInstance.id}^FS\n`;

        // ==============        task checklist (linear projects only)         ======================================
        const linearTaskList = labelInstance.linearTaskList || [];
        if (linearTaskList.length > 0) {
            const tasksStartY = middleY + 25;
            const tasksEndY = bottomY - 15;
            const availableHeight = tasksEndY - tasksStartY;

            let checkboxSize = 40;
            let textFontSize = 40;
            let rowHeight = 55;
            let colorFontSize = 28;
            let colorExtraHeight = 35;

            const colorRows = linearTaskList.filter(t => !!t.color).length;
            const idealHeight = linearTaskList.length * rowHeight + colorRows * colorExtraHeight;
            if (idealHeight > availableHeight) {
                const scale = availableHeight / idealHeight;
                checkboxSize = Math.max(20, Math.floor(checkboxSize * scale));
                textFontSize = Math.max(20, Math.floor(textFontSize * scale));
                rowHeight = Math.max(26, Math.floor(rowHeight * scale));
                colorFontSize = Math.max(18, Math.floor(colorFontSize * scale));
                colorExtraHeight = Math.max(22, Math.floor(colorExtraHeight * scale));
            }

            const checkboxX = labelX + 10;
            const textX = checkboxX + checkboxSize + 15;
            const boxThickness = 3;

            let currentY = tasksStartY;
            linearTaskList.forEach((task) => {
                // Checkbox outline
                zpl += `^FO${checkboxX},${currentY}^GB${checkboxSize},${checkboxSize},${boxThickness}^FS\n`;
                if (task.completed) {
                    // Draw an X inside the box using two diagonal lines
                    zpl += `^FO${checkboxX},${currentY}^GD${checkboxSize},${checkboxSize},${boxThickness},B,R^FS\n`;
                    zpl += `^FO${checkboxX},${currentY}^GD${checkboxSize},${checkboxSize},${boxThickness},B,L^FS\n`;
                }
                const textY = currentY + Math.max(0, Math.floor((checkboxSize - textFontSize) / 2));
                zpl += `^FO${textX},${textY}\n^A0N,${textFontSize},${textFontSize}\n^FD${task.type}^FS\n`;
                currentY += rowHeight;

                if (task.color) {
                    const colorLift = Math.max(8, Math.floor(rowHeight * 0.25));
                    zpl += `^FO${textX},${currentY - colorLift}\n^A0N,${colorFontSize},${colorFontSize}\n^FD${task.color}^FS\n`;
                    currentY += colorExtraHeight - colorLift;
                }
            });
        }

        // ==============        project code at bottom (centered)         ======================================
        zpl += `^FO0,${bottomY}^GB${lineWidth},2,2^FS\n`;
        zpl += `^FO0,${bottomY + 50}\n^A0N,160,160\n^FB800,1,0,C,0\n^FD${labelInstance.projectCode}^FS\n`;

        zpl += `^XZ`;
        return zpl;
    }

    if (labelInstance.type === LabelType.InjectionMoldingProduct
        || labelInstance.type === LabelType.InjectionMoldingTraceability) {
        const labelWidth = 812;
        const labelHeight = 1218;
        const margin = 45;
        const contentWidth = labelWidth - (margin * 2);
        const printQuantity = Math.max(1, Math.floor(labelInstance.printQuantity || 1));
        const printedOn = new Date(labelInstance.printDate);
        const date = `${`${printedOn.getMonth() + 1}`.padStart(2, '0')}/${`${printedOn.getDate()}`.padStart(2, '0')}/${printedOn.getFullYear()}`;
        const customerName = sanitizeForZpl(labelInstance.customerName || '');
        const customerPartNumber = sanitizeForZpl(labelInstance.customerPartNumber || '');
        const partNumber = sanitizeForZpl(labelInstance.partNumber || '');
        const description = sanitizeForZpl(labelInstance.description || '');

        if (labelInstance.type === LabelType.InjectionMoldingTraceability) {
            const lots = labelInstance.lotTraceability ?? [];
            const lotStartY = 190;
            const lotAreaHeight = labelHeight - lotStartY - margin;
            const lotRowHeight = Math.floor(lotAreaHeight / Math.max(lots.length, 1));
            const maxKeyFont = Math.min(40, Math.max(24, Math.floor(lotRowHeight * 0.22)));
            const maxValueFont = Math.min(78, Math.max(38, Math.floor(lotRowHeight * 0.44)));

            let zpl = `^XA\n^PW${labelWidth}\n^LL${labelHeight}\n`;
            zpl += `^FO${margin},55\n^A0N,64,64\n^FB${contentWidth},1,0,C,0\n^FDCOMPONENT LOTS^FS\n`;
            zpl += `^FO${margin},150^GB${contentWidth},4,4^FS\n`;

            if (!lots.length) {
                zpl += `^FO${margin},${lotStartY + 80}\n^A0N,42,42\n^FB${contentWidth},2,8,C,0\n^FDNO LOT NUMBERS RECORDED^FS\n`;
            } else {
                lots.forEach((entry, index) => {
                    const y = lotStartY + (index * lotRowHeight);
                    const key = sanitizeForZpl(entry.key || 'LOT').toUpperCase();
                    const value = sanitizeForZpl(entry.value || '-');
                    const keyFont = fitZplFontSize(key, contentWidth, maxKeyFont, 20);
                    const valueFont = fitZplFontSize(value, contentWidth, maxValueFont, 20);
                    zpl += `^FO${margin},${y}\n^A0N,${keyFont},${keyFont}\n^FD${key}^FS\n`;
                    zpl += `^FO${margin},${y + keyFont + 12}\n^A0N,${valueFont},${valueFont}\n^FB${contentWidth},1,0,L,0\n^FD${value}^FS\n`;
                    if (index < lots.length - 1) {
                        zpl += `^FO${margin},${y + lotRowHeight - 16}^GB${contentWidth},2,2^FS\n`;
                    }
                });
            }

            zpl += `^PQ${printQuantity}\n^XZ`;
            return zpl;
        }

        const logoHeight = 90;
        const logoBlob = await fetch('/assets/images/label-logos/seljan_logo_plain_bw.png').then(response => response.blob());
        const logoImg = await blobToImage(logoBlob);
        const logoGraphic = imageToZPL(toMonochrome(logoImg, 50, logoHeight));
        const logoX = Math.round((labelWidth - logoGraphic.width) / 2);

        let zpl = `^XA\n^PW${labelWidth}\n^LL${labelHeight}\n`;
        zpl += `^FO${logoX},25\n${logoGraphic.zpl}^FS\n`;
        zpl += `^FO${margin},125^GB${contentWidth},3,3^FS\n`;

        let y = 165;
        const addField = (label: string, value: string, lines = 1): void => {
            zpl += `^FO${margin},${y}\n^A0N,28,28\n^FD${label}^FS\n`;
            y += 34;
            zpl += `^FO${margin},${y}\n^A0N,48,48\n^FB${contentWidth},${lines},5,L,0\n^FD${value || '-'}^FS\n`;
            y += (lines * 58) + 24;
        };

        addField('CUSTOMER', customerName);
        addField('CUSTOMER PART NUMBER', customerPartNumber);
        addField('DESCRIPTION', description, 2);
        addField('PART NUMBER', partNumber);

        zpl += `^FO${margin},${y}^GB${contentWidth},3,3^FS\n`;
        y += 30;
        zpl += `^FO${margin},${y}\n^A0N,34,34\n^FDDATE^FS\n`;
        zpl += `^FO${margin + 340},${y}\n^A0N,34,34\n^FDQTY^FS\n`;
        y += 44;
        zpl += `^FO${margin},${y}\n^A0N,58,58\n^FD${date}^FS\n`;
        zpl += `^FO${margin + 340},${y}\n^A0N,58,58\n^FD${labelInstance.quantity}^FS\n`;
        y += 92;

        zpl += `^PQ${printQuantity}\n^XZ`;
        return zpl;
    }

    if (labelInstance.type === LabelType.MetalsPackUnit) {
        const labelWidth = 812;
        const labelHeight = 1218;
        const labelX = 30;
        const textWidth = labelWidth - (2 * labelX);
        const printQuantity = Math.max(1, Math.floor(labelInstance.printQuantity || 1));
        const printedOn = new Date(labelInstance.printDate);
        const date = `${`${printedOn.getMonth() + 1}`.padStart(2, '0')}/${`${printedOn.getDate()}`.padStart(2, '0')}/${printedOn.getFullYear()}`;
        const customerName = sanitizeForZpl(labelInstance.customerName || '');
        const partNumber = sanitizeForZpl(labelInstance.partNumber || '');
        const description = sanitizeForZpl(labelInstance.description || '');
        const unit = sanitizeForZpl((labelInstance.code || 'BOX').toUpperCase());
        const quantityText = String(labelInstance.quantity ?? '');

        // Use the full-width dividers and two-column scan area of Prism's
        // packing-slip labels, rather than the metals product label's underlined fields.
        const logoHeight = 100;
        const logoBlob = await fetch('/assets/images/label-logos/seljan_logo_plain_bw.png').then(response => response.blob());
        const logoImg = await blobToImage(logoBlob);
        const logoGraphic = imageToZPL(toMonochrome(logoImg, 50, logoHeight));
        const logoX = Math.round((labelWidth - logoGraphic.width) / 2);

        let zpl = `^XA\n^PW${labelWidth}\n^LL${labelHeight}\n`;
        zpl += `^FO${logoX},20\n${logoGraphic.zpl}^FS\n`;
        zpl += `^FO0,145^GB${labelWidth},4,4^FS\n`;

        const customerFont = fitZplFontSize(customerName, textWidth, 52, 28);
        zpl += `^FO${labelX},175\n^A0N,${customerFont},${customerFont}\n^FB${textWidth},1,0,L,0\n^FD${customerName || '-'}^FS\n`;

        const partFont = fitZplFontSize(partNumber, textWidth, 100, 38);
        zpl += `^FO${labelX},250\n^A0N,30,30\n^FDPART NUMBER^FS\n`;
        zpl += `^FO${labelX},290\n^A0N,${partFont},${partFont}\n^FB${textWidth},1,0,L,0\n^FD${partNumber || '-'}^FS\n`;

        // The packing-slip labels show the description as content, not an
        // underlined field heading.
        zpl += `^FO${labelX},425\n^A0N,48,48\n^FB${textWidth},2,6,L,0\n^FD${description || '-'}^FS\n`;
        zpl += `^FO0,560^GB${labelWidth},4,4^FS\n`;
        zpl += `^FO405,560^GB4,${labelHeight - 560},4^FS\n`;

        const quantityFont = fitZplFontSize(quantityText, 350, 150, 60);
        const unitFont = fitZplFontSize(unit, 350, 70, 40);
        zpl += `^FO0,625\n^A0N,50\n^FB405,1,0,C,0\n^FDQTY^FS\n`;
        zpl += `^FO25,705\n^A0N,${quantityFont}\n^FB355,1,0,C,0\n^FD${quantityText}^FS\n`;
        zpl += `^FO25,865\n^A0N,${unitFont}\n^FB355,1,0,C,0\n^FD${unit}^FS\n`;

        if (labelInstance.split) {
            zpl += `^FO0,970\n^A0N,55\n^FB405,1,0,C,0\n^FDPARTIAL^FS\n`;
        }
        zpl += `^FO0,1060\n^A0N,30\n^FB405,1,0,C,0\n^FDDATE^FS\n`;
        zpl += `^FO0,1100\n^A0N,44\n^FB405,1,0,C,0\n^FD${date}^FS\n`;

        // A raster QR has a known physical size, so it stays centered even when
        // a different label ID would make Zebra's native QR use another version.
        // Keep the full persisted ID only in the QR payload.
        const labelId = sanitizeForZpl(labelInstance.id);
        const qrSize = 320;
        const qrBlob = await generateQrBlob(labelId, qrSize);
        const qrZpl = await convertBlobToZPL(qrBlob, 50, qrSize);
        const rightPanelX = 409;
        const rightPanelY = 564;
        const qrX = Math.round(rightPanelX + (labelWidth - rightPanelX - qrSize) / 2);
        const qrY = Math.round(rightPanelY + (labelHeight - rightPanelY - qrSize) / 2);
        zpl += `^FO${qrX},${qrY}\n${qrZpl}^FS\n`;

        zpl += `^PQ${printQuantity}\n^XZ`;
        return zpl;
    }

    if (labelInstance.type === LabelType.MetalsInventoryBin) {
        // Landscape on the 4x6 stock: every field is rotated 90° clockwise (^A0R), so the
        // label reads 6" wide x 4" tall once it is turned onto the bin. Laid out in landscape
        // coordinates (x across, y down) and mapped onto the stock by the helpers below.
        // The bin code fills the left column; product details and a small QR fill the right.
        const stockWidth = 812;
        const stockHeight = 1218;
        const width = stockHeight;
        const height = stockWidth;
        const printQuantity = Math.max(1, Math.floor(labelInstance.printQuantity || 1));
        const rackName = sanitizeForZpl((labelInstance.rackName || '').toUpperCase());
        const partNumber = sanitizeForZpl(labelInstance.partNumber || '');
        const description = sanitizeForZpl(labelInstance.description || '');
        const binCode = sanitizeForZpl(labelInstance.binCode || '');

        // Landscape (x, y) is stock (height - y - fieldHeight, x); text tops face the stock's right edge.
        const text = (x: number, y: number, fontSize: number, value: string, blockWidth: number, justify: 'L' | 'C' | 'R' = 'L', lines = 1) => {
            const spacing = 6;
            const blockHeight = lines * fontSize + (lines - 1) * spacing;
            return `^FO${height - y - blockHeight},${x}\n^A0R,${fontSize},${fontSize}\n^FB${blockWidth},${lines},${spacing},${justify},0\n^FD${value}^FS\n`;
        };
        const verticalLine = (x: number, y0: number, y1: number, thickness = 4) =>
            `^FO${height - y1},${x}^GB${y1 - y0},${thickness},${thickness}^FS\n`;

        let zpl = `^XA\n^PW${stockWidth}\n^LL${stockHeight}\n`;

        // ============  bin code (left column, centered vertically)  ============
        const leftColumnWidth = 440;
        zpl += verticalLine(leftColumnWidth, 0, height);
        const binFont = fitZplFontSize(binCode, 400, 330, 80);
        const binGroupHeight = 50 + 30 + binFont;
        const binGroupY = Math.round((height - binGroupHeight) / 2);
        zpl += text(0, binGroupY, 50, 'BIN', leftColumnWidth, 'C');
        zpl += text(20, binGroupY + 80, binFont, binCode || '-', 400, 'C');

        // ============  rack and product details (right column)  ============
        const rightX = leftColumnWidth + 30;
        const rightWidth = width - 30 - rightX;
        const partFont = fitZplFontSize(partNumber, rightWidth, 120, 40);
        zpl += text(rightX, 35, 40, rackName || '-', rightWidth);
        zpl += text(rightX, 125, 30, 'PART NUMBER', rightWidth);
        zpl += text(rightX, 165, partFont, partNumber || '-', rightWidth);
        zpl += text(rightX, 320, 48, description || '-', rightWidth, 'L', 2);

        // The preview rotates the stock counterclockwise. Its lower right corner maps to
        // the stock's lower left, so the raster QR starts near the stock's left edge.
        const qrSize = 180;
        const qrMargin = 30;
        const qrBlob = await generateQrBlob(sanitizeForZpl(labelInstance.id), qrSize);
        const qrZpl = await convertBlobToZPL(qrBlob, 50, qrSize);
        zpl += `^FO${qrMargin},${width - qrMargin - qrSize}\n${qrZpl}^FS\n`;

        zpl += `^PQ${printQuantity}\n^XZ`;
        return zpl;
    }

    if (labelInstance.type === LabelType.MetalsInventoryBundle) {
        // Pack-label frame for the bundles that sit inside a bin: no rack or bin position
        // printed (bundles move between bins; the saved instance remembers where it was
        // printed from), and the left column carries the bundle quantity.
        const labelWidth = 812;
        const labelHeight = 1218;
        const labelX = 30;
        const textWidth = labelWidth - (2 * labelX);
        const printQuantity = Math.max(1, Math.floor(labelInstance.printQuantity || 1));
        const partNumber = sanitizeForZpl(labelInstance.partNumber || '');
        const description = sanitizeForZpl(labelInstance.description || '');
        const quantityText = labelInstance.quantity > 0 ? String(Math.floor(labelInstance.quantity)) : '-';

        const logoHeight = 100;
        const logoBlob = await fetch('/assets/images/label-logos/seljan_logo_plain_bw.png').then(response => response.blob());
        const logoImg = await blobToImage(logoBlob);
        const logoGraphic = imageToZPL(toMonochrome(logoImg, 50, logoHeight));
        const logoX = Math.round((labelWidth - logoGraphic.width) / 2);

        let zpl = `^XA\n^PW${labelWidth}\n^LL${labelHeight}\n`;
        zpl += `^FO${logoX},20\n${logoGraphic.zpl}^FS\n`;
        zpl += `^FO0,145^GB${labelWidth},4,4^FS\n`;

        const partFont = fitZplFontSize(partNumber, textWidth, 100, 38);
        zpl += `^FO${labelX},195\n^A0N,30,30\n^FDPART NUMBER^FS\n`;
        zpl += `^FO${labelX},235\n^A0N,${partFont},${partFont}\n^FB${textWidth},1,0,L,0\n^FD${partNumber || '-'}^FS\n`;
        zpl += `^FO${labelX},380\n^A0N,48,48\n^FB${textWidth},2,6,L,0\n^FD${description || '-'}^FS\n`;
        zpl += `^FO0,560^GB${labelWidth},4,4^FS\n`;
        zpl += `^FO405,560^GB4,${labelHeight - 560},4^FS\n`;

        const quantityFont = fitZplFontSize(quantityText, 355, 280, 80);
        zpl += `^FO0,660\n^A0N,50\n^FB405,1,0,C,0\n^FDQTY^FS\n`;
        zpl += `^FO25,${760 + Math.round((280 - quantityFont) / 2)}\n^A0N,${quantityFont},${quantityFont}\n^FB355,1,0,C,0\n^FD${quantityText}^FS\n`;

        // Raster QR (fixed physical size) of the label instance id, centered in the right column.
        const qrSize = 320;
        const qrBlob = await generateQrBlob(sanitizeForZpl(labelInstance.id), qrSize);
        const qrZpl = await convertBlobToZPL(qrBlob, 50, qrSize);
        const rightPanelX = 409;
        const rightPanelY = 564;
        const qrX = Math.round(rightPanelX + (labelWidth - rightPanelX - qrSize) / 2);
        const qrY = Math.round(rightPanelY + (labelHeight - rightPanelY - qrSize) / 2);
        zpl += `^FO${qrX},${qrY}\n${qrZpl}^FS\n`;

        zpl += `^PQ${printQuantity}\n^XZ`;
        return zpl;
    }

    if (labelInstance.type === LabelType.MetalsProduct) {
        const labelWidth = 812;
        const labelHeight = 1218;
        const labelX = 60;
        const textWidth = labelWidth - (2 * labelX);

        const partNumber = sanitizeForZpl(labelInstance.partNumber || '');
        const description = sanitizeForZpl(labelInstance.description || '');
        const purchaseOrderNumber = sanitizeForZpl(labelInstance.purchaseOrderNumber || '');
        const printQuantity = Math.max(1, Math.floor(labelInstance.printQuantity || 1));

        // The SELJAN wordmark is the logo asset; the address line under it is text.
        const logoHeight = 190;
        const logoBlob = await fetch('/assets/images/label-logos/seljan_logo_plain_bw.png').then(response => response.blob());
        const logoImg = await blobToImage(logoBlob);
        const logoWidth = Math.round(logoImg.width * (logoHeight / logoImg.height));
        const logoZpl = imageToZPL(toMonochrome(logoImg, 50, logoHeight)).zpl;
        const logoX = Math.round((labelWidth - logoWidth) / 2);

        let zpl = `^XA\n^PW${labelWidth}\n^LL${labelHeight}\n`;

        // ============  SELJAN wordmark (centered)  ============
        zpl += `^FO${logoX},50\n${logoZpl}^FS\n`;

        // ============  Company, Inc.        Lake Mills, Wi 53551  ============
        zpl += `^FO${labelX},240\n^A0N,40\n^FDCompany, Inc.^FS\n`;
        zpl += `^FO${labelX + 340},240\n^A0N,40\n^FB${textWidth - 340},1,0,R,0\n^FDLake Mills, WI 53551^FS\n`;

        // ============  Part#  ============
        // The part number NEVER wraps — a part number split across two lines reads as two
        // part numbers to the customer. A long one shrinks to fit on one line instead.
        const bodyFont = 62;
        const partText = `Part#: ${partNumber}`;
        const partFont = fitZplFontSize(partText, textWidth, bodyFont, 34);

        let y = 340;
        // ^FO anchors the top of the character cell, so a shrunken line would ride high in
        // the space a full-size one occupies — nudge it down to sit centered in that slot.
        const partY = y + Math.round((bodyFont - partFont) / 2);
        zpl += `^FO${labelX},${partY}\n^A0N,${partFont}\n^FD${partText}^FS\n`;
        // Everything below always starts where it would for a full-size part number, so a
        // shrunken one doesn't drag the rest of the label up with it.
        y += bodyFont + 10 + 48;

        // ============  Description (underlined heading + wrapped body)  ============
        zpl += `^FO${labelX},${y}\n^A0N,${bodyFont}\n^FDDescription:^FS\n`;
        zpl += `^FO${labelX},${y + 58}^GB290,3,3^FS\n`;
        zpl += `^FO${labelX},${y + 80}\n^A0N,46\n^FB${textWidth},3,4,L,0\n^FD${description}^FS\n`;
        // Reserve the full 3 lines the description is allowed, so a short one doesn't
        // pull the quantity up into it.
        y += 80 + 220;

        // ============  Quantity  ============
        zpl += `^FO${labelX},${y}\n^A0N,${bodyFont}\n^FDQuantity: ${labelInstance.quantity} pcs.^FS\n`;
        y += 120;

        // ============  P.O.#  ============
        zpl += `^FO${labelX},${y}\n^A0N,${bodyFont}\n^FDP.O.#: ${purchaseOrderNumber}^FS\n`;

        // ============  MADE IN USA (centered, bold)  ============
        // Sits at a fixed spot near the bottom, but gets pushed down if a wrapped part
        // number has driven the P.O. line into it.
        const madeInUsaY = Math.min(1140, Math.max(1030, y + 90));
        // ZPL scalable fonts have no bold weight — print the text twice, offset by a
        // pixel, to thicken the strokes the way the printed label looks.
        zpl += `^FO0,${madeInUsaY}\n^A0N,58\n^FB${labelWidth},1,0,C,0\n^FDMADE IN USA^FS\n`;
        zpl += `^FO2,${madeInUsaY + 1}\n^A0N,58\n^FB${labelWidth},1,0,C,0\n^FDMADE IN USA^FS\n`;

        zpl += `^PQ${printQuantity}\n`;
        zpl += `^XZ`;
        return zpl;
    }

    // 2. Add this code block to your label generation function
    if (labelInstance.type === LabelType.PlasticsInventoryBin) {

        // Layout constants
        const splitGap = 80;
        const borderInset = 15;
        const borderThickness = 8;
        const padding = 20;
        const labelWidth = 812;
        const labelHeight = 1218;
        const centerY = 609;

        let zpl = '^XA\n';

        const partNumberFontSize = 140;
        const splitDescriptionFontSize = 35;
        const sectionHeight = centerY;
        const innerWidth = labelWidth - (2 * borderInset);
        const innerSectionHeight = sectionHeight - (2 * borderInset);

        // Helper function to draw border for a section
        const drawSectionBorder = (startY: number): string => {
            let borderZpl = '';
            borderZpl += `^FO${borderInset},${startY + borderInset}^GB${innerWidth},${borderThickness},${borderThickness},B^FS\n`;
            borderZpl += `^FO${borderInset},${startY + borderInset + innerSectionHeight - borderThickness}^GB${innerWidth},${borderThickness},${borderThickness},B^FS\n`;
            borderZpl += `^FO${borderInset},${startY + borderInset}^GB${borderThickness},${innerSectionHeight},${borderThickness},B^FS\n`;
            borderZpl += `^FO${borderInset + innerWidth - borderThickness},${startY + borderInset}^GB${borderThickness},${innerSectionHeight},${borderThickness},B^FS\n`;
            return borderZpl;
        };

        // Helper function to generate ZPL for one section
        const generateSectionZpl = (startY: number): string => {
            let sectionZpl = '';
            const labelX = borderInset + padding;
            let sectionY = startY + borderInset + padding;

            const textMaxWidth = (borderInset + innerWidth) - labelX - padding;

            // Part number (large, top left)
            sectionZpl += `^FO${labelX},${sectionY}\n^A0N,${partNumberFontSize},${partNumberFontSize}\n^FD${labelInstance.partNumber}^FS\n`;
            sectionY += partNumberFontSize + 30;

            // Description (with wrapping)
            sectionZpl += `^FO${labelX},${sectionY}\n^A0N,${splitDescriptionFontSize},${splitDescriptionFontSize}\n^FB${textMaxWidth},4,0,L,0\n^FD${labelInstance.description || ''}^FS\n`;
            sectionY += splitGap;

            // Customer description
            sectionZpl += `^FO${labelX},${sectionY}\n^A0N,${splitDescriptionFontSize},${splitDescriptionFontSize}\n^FB${textMaxWidth},4,0,L,0\n^FD${labelInstance.vendorHardwareDataDescription}^FS\n`;

            return sectionZpl;
        };

        // ========== TOP HALF ==========
        zpl += drawSectionBorder(0);
        zpl += generateSectionZpl(0);

        // ========== BOTTOM HALF ==========
        zpl += drawSectionBorder(centerY);
        zpl += generateSectionZpl(centerY);

        zpl += `^XZ`;
        return zpl;
    }

    if (labelInstance.type === LabelType.PlasticsKit) {

        // Layout constants (same as PlasticsInventoryBin)
        const borderInset = 15;
        const borderThickness = 8;
        const padding = 20;
        const labelWidth = 812;
        const centerY = 609;

        let zpl = '^XA\n';

        const partNumberFontSize = 100;
        const splitDescriptionFontSize = 35;
        const countFontSize = 60;
        const sectionHeight = centerY;
        const innerWidth = labelWidth - (2 * borderInset);
        const innerSectionHeight = sectionHeight - (2 * borderInset);

        const qrSize = 400;
        const qrBlob = await generateQrBlob(labelInstance.kitId, qrSize);
        const qrZpl = await convertBlobToZPL(qrBlob, 50);

        const codeFontSize = 120;

        const partNumber = sanitizeForZpl(labelInstance.partNumber);
        const description = sanitizeForZpl(labelInstance.description || '');

        const labelX = borderInset + padding;


        const drawSectionBorder = (startY: number): string => {
            let borderZpl = '';
            borderZpl += `^FO${borderInset},${startY + borderInset}^GB${innerWidth},${borderThickness},${borderThickness},B^FS\n`;
            borderZpl += `^FO${borderInset},${startY + borderInset + innerSectionHeight - borderThickness}^GB${innerWidth},${borderThickness},${borderThickness},B^FS\n`;
            borderZpl += `^FO${borderInset},${startY + borderInset}^GB${borderThickness},${innerSectionHeight},${borderThickness},B^FS\n`;
            borderZpl += `^FO${borderInset + innerWidth - borderThickness},${startY + borderInset}^GB${borderThickness},${innerSectionHeight},${borderThickness},B^FS\n`;
            return borderZpl;
        };

        const generateSectionZpl = (startY: number): string => {
            let sectionZpl = '';
            let sectionY = startY + borderInset + padding;

            const textMaxWidth = (borderInset + innerWidth) - labelX - padding;

            // Part number (large, top left)
            sectionZpl += `^FO${labelX},${sectionY}\n^A0N,${partNumberFontSize},${partNumberFontSize}\n^FD${partNumber}^FS\n`;

            // Product count (aligned with part number baseline)
            sectionZpl += `^FO${labelX + textMaxWidth - 200},${sectionY}\n^A0N,${countFontSize},${countFontSize}\n^FB200,1,0,R,0\n^FDx${labelInstance.productCount}^FS\n`;
            sectionY += partNumberFontSize + 10;

            // Description (with wrapping)
            sectionZpl += `^FO${labelX},${sectionY}\n^A0N,${splitDescriptionFontSize},${splitDescriptionFontSize}\n^FB${textMaxWidth},4,0,L,0\n^FD${description}^FS\n`;

            // Bottom row: code (left) + QR code (right)
            const sectionBottomY = startY + borderInset + innerSectionHeight - padding;
            const descBottomY = sectionY + (splitDescriptionFontSize * 4);
            const bottomSpace = sectionBottomY - descBottomY;
            const codeY = descBottomY + (bottomSpace - codeFontSize) / 2;

            // Code text (large, left)
            sectionZpl += `^FO${labelX + 40},${Math.round(codeY)}\n^A0N,${codeFontSize},${codeFontSize}\n^FD${labelInstance.code}^FS\n`;

            const qrX = borderInset + innerWidth - padding - qrSize + 15;
            const qrY = descBottomY + (bottomSpace - qrSize) / 2 - 35;

            sectionZpl += `^FO${Math.round(qrX)},${Math.round(qrY)}\n${qrZpl}^FS\n`;

            return sectionZpl;
        };

        // ========== TOP HALF ==========
        zpl += drawSectionBorder(0);
        zpl += generateSectionZpl(0);

        // ========== BOTTOM HALF ==========
        zpl += drawSectionBorder(centerY);
        zpl += generateSectionZpl(centerY);

        zpl += `^XZ`;
        return zpl;
    }

    if (labelInstance.type === LabelType.PlasticsInventoryBinMulti) {
        const borderThickness = 8;
        const padding = 20;
        const labelWidth = 812;
        const labelHeight = 1218;
        const contentWidth = Math.round(labelWidth * 0.65);
        const borderX = 15;
        const borderWidth = contentWidth - (2 * borderX);
        const innerHeight = labelHeight - (2 * borderX);
        const labelX = borderX + padding;
        const textMaxWidth = borderWidth - (2 * padding);

        const items = labelInstance.items;
        const leftItems = items.slice(0, 7);
        const rightItems = items.slice(7, 9);
        const leftCount = leftItems.length;
        const rowHeight = Math.floor(innerHeight / 7);
        const totalContentHeight = rowHeight * leftCount;

        let zpl = '^XA\n';

        // Draw left column border sized to actual item count
        zpl += `^FO${borderX},${borderX}^GB${borderWidth},${borderThickness},${borderThickness},B^FS\n`;
        zpl += `^FO${borderX},${borderX + totalContentHeight - borderThickness}^GB${borderWidth},${borderThickness},${borderThickness},B^FS\n`;
        zpl += `^FO${borderX},${borderX}^GB${borderThickness},${totalContentHeight},${borderThickness},B^FS\n`;
        zpl += `^FO${borderX + borderWidth - borderThickness},${borderX}^GB${borderThickness},${totalContentHeight},${borderThickness},B^FS\n`;

        // Left column items (up to 7)
        for (let i = 0; i < leftCount; i++) {
            const item = leftItems[i];
            const rowStartY = borderX + (i * rowHeight);

            // Separator line between rows (skip first)
            if (i > 0) {
                zpl += `^FO${borderX},${rowStartY}^GB${borderWidth},4,4^FS\n`;
            }

            const partNumberY = rowStartY + padding + 10;
            const descriptionY = partNumberY + 60;

            // Part number (large)
            zpl += `^FO${labelX},${partNumberY}\n^A0N,50,50\n^FB${textMaxWidth},1,0,L,0\n^FD${item.partNumber}^FS\n`;

            // Description (clamp to ~2 lines with ellipsis)
            const charsPerLine = Math.floor(textMaxWidth / 15);
            const maxChars = charsPerLine * 2;
            const desc = item.description.length > maxChars
                ? item.description.substring(0, maxChars - 3) + '...'
                : item.description;
            zpl += `^FO${labelX},${descriptionY}\n^A0N,30,30\n^FB${textMaxWidth},2,0,L,0\n^FD${desc}^FS\n`;
        }

        // Right column items (items 8-9), rotated 90° — exact same cell size as left column rows
        if (rightItems.length > 0) {
            // A left-column cell is borderWidth wide × rowHeight tall.
            // Rotated 90°, each right cell is rowHeight wide × borderWidth tall.
            const cellWidth = rowHeight; // short side (was height, now width)
            const cellHeight = borderWidth; // long side (was width, now height)
            const rightStartX = contentWidth + borderX;
            const rightStartY = borderX;

            for (let i = 0; i < rightItems.length; i++) {
                const item = rightItems[i];
                const cellY = rightStartY + (i * cellHeight);

                // Cell border
                zpl += `^FO${rightStartX},${cellY}^GB${cellWidth},${borderThickness},${borderThickness},B^FS\n`;
                zpl += `^FO${rightStartX},${cellY + cellHeight - borderThickness}^GB${cellWidth},${borderThickness},${borderThickness},B^FS\n`;
                zpl += `^FO${rightStartX},${cellY}^GB${borderThickness},${cellHeight},${borderThickness},B^FS\n`;
                zpl += `^FO${rightStartX + cellWidth - borderThickness},${cellY}^GB${borderThickness},${cellHeight},${borderThickness},B^FS\n`;

                // With ^A0R, text rotates 90° CW: anchor is bottom-left of the text.
                // "Left" in the rotated cell (visually top) = description
                // "Right" in the rotated cell (visually bottom) = part number
                const rotatedTextMaxWidth = cellHeight - (2 * padding);

                // Description (to the left / visually on top)
                const descX = rightStartX + padding;
                zpl += `^FO${descX},${cellY + padding}\n^A0R,30,30\n^FB${rotatedTextMaxWidth},2,0,L,0\n`;
                const charsPerLine = Math.floor(rotatedTextMaxWidth / 18);
                const maxChars = charsPerLine * 2;
                const desc = item.description.length > maxChars
                    ? item.description.substring(0, maxChars - 3) + '...'
                    : item.description;
                zpl += `^FD${desc}^FS\n`;

                // Part number (to the right / visually on bottom)
                const partX = descX + 60;
                zpl += `^FO${partX},${cellY + padding}\n^A0R,50,50\n^FB${rotatedTextMaxWidth},1,0,L,0\n^FD${item.partNumber}^FS\n`;
            }
        }

        zpl += `^XZ`;
        return zpl;
    }

    if (labelInstance.type === LabelType.ObsessedEngineeringProduct) {
        const labelSize = 203;
        const qrSize = 132;
        const qrBlob = await generateQrBlob(labelInstance.id, qrSize);
        const qrZpl = await convertBlobToZPL(qrBlob, 50, qrSize);
        const partNumber = sanitizeForZpl(labelInstance.partNumber);
        const serialCode = sanitizeForZpl(labelInstance.serialCode ?? '');
        const qrX = Math.round((labelSize - qrSize) / 2);

        let zpl = '^XA\n';
        zpl += `^PW${labelSize}\n^LL${labelSize}\n`;
        zpl += `^FO${qrX},8\n${qrZpl}^FS\n`;
        if (serialCode) {
            zpl += `^FO0,55\n^A0R,35,45\n^FD${serialCode}^FS\n`;
        }
        zpl += `^FO0,152\n^A0N,30,30\n^FB${labelSize},1,0,C,0\n^FD${partNumber}^FS\n`;
        zpl += '^XZ';

        return zpl;
    }

    if (labelInstance.type === LabelType.Shipment) {
        const labelWidth = 812;
        const labelHeight = 1218;
        const labelX = 40;
        const ruleWidth = labelWidth - (2 * labelX);

        // 4 character code (last 4 chars of the shipment id) e.g. "a083"
        const code = parseLabelId(labelInstance.shipmentId);
        const carrierName = sanitizeForZpl(labelInstance.carrierName || 'Best Way');
        const shipDateStr = labelInstance.shipDate
            ? new Date(labelInstance.shipDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
            })
            : '';

        // Ship-to address: first line is the name/company (emphasized), the rest
        // are the street/city lines. Split, sanitize, and drop blank lines.
        const addressLines = (labelInstance.shippingAddress || '')
            .split('\n')
            .map(line => sanitizeForZpl(line.trim()))
            .filter(line => line.length > 0);
        const addressName = addressLines[0] || '';
        const addressRest = addressLines.slice(1);

        let zpl = `^XA\n^PW${labelWidth}\n^LL${labelHeight}\n`;
        let y = 50;

        // Height-only font sizes (no forced width) so ^FB ,C, centers the glyphs'
        // natural advance box — forcing the width pads each char into a fixed cell
        // and dumps the slack as trailing space, shoving the text off-center.

        // "SHIPMENT" header (centered)
        zpl += `^FO0,${y}\n^A0N,50\n^FB${labelWidth},1,0,C,0\n^FDSHIPMENT^FS\n`;
        y += 70;

        // Shipment code (large, centered)
        zpl += `^FO0,${y}\n^A0N,180\n^FB${labelWidth},1,0,C,0\n^FD${code}^FS\n`;
        y += 180;

        // Divider
        zpl += `^FO${labelX},${y}^GB${ruleWidth},3,3^FS\n`;
        y += 35;

        // Carrier (centered) e.g. "FedEx"
        zpl += `^FO0,${y}\n^A0N,65\n^FB${labelWidth},1,0,C,0\n^FD${carrierName}^FS\n`;
        y += 85;

        // Ship date (centered) e.g. "06/09/2026"
        zpl += `^FO0,${y}\n^A0N,42\n^FB${labelWidth},1,0,C,0\n^FD${shipDateStr}^FS\n`;
        y += 60;

        // --- Fixed QR (always the same size, bottom-anchored) -------------------
        // The QR never resizes for a long address; instead the ship-to block above
        // shrinks its font / line spacing to fit the space left above the QR.
        const bottomMargin = 20;
        const qrSize = 420;
        const qrY = labelHeight - bottomMargin - qrSize;
        // Divider just above the QR; the ship-to band is everything between the ship
        // date and this line.
        const qrDividerY = qrY - 35;

        // SHIP TO section, fit into the band between the ship date and the QR
        // divider. Long / multi-line addresses scale the font + line advances down
        // so the block always fits without moving or shrinking the QR.
        if (addressName) {
            const bandTop = y + 15;
            const bandHeight = Math.max(0, (qrDividerY - 20) - bandTop);
            // Width budget for centered text, kept off the side edges.
            const usableWidth = labelWidth - (2 * labelX);
            // Largest font (height) at which `text` still fits one line in
            // usableWidth (~0.58 advance per unit of height for this typeface),
            // never above the given default.
            const widthFitFont = (text: string, defaultFont: number): number => {
                if (!text) return defaultFont;
                const fit = Math.floor(usableWidth / (text.length * 0.58));
                return Math.max(1, Math.min(defaultFont, fit));
            };
            const longestRest = addressRest.reduce((max, l) => Math.max(max, l.length), 0);

            // Defaults, width-capped so a long single line can't overflow the edges.
            let captionFont = 30;
            let nameFont = widthFitFont(addressName, 52);
            let lineFont = widthFitFont('x'.repeat(longestRest), 38);
            let captionAdvance = 44;
            let nameAdvance = nameFont + 12;
            let lineAdvance = lineFont + 10;

            // Vertical fit: if the stack is taller than the band, scale it all down
            // proportionally (with floors so it stays legible).
            const stackHeight = captionAdvance + nameAdvance + addressRest.length * lineAdvance;
            if (bandHeight > 0 && stackHeight > bandHeight) {
                const scale = bandHeight / stackHeight;
                captionFont = Math.max(20, Math.floor(captionFont * scale));
                nameFont = Math.max(28, Math.floor(nameFont * scale));
                lineFont = Math.max(22, Math.floor(lineFont * scale));
                captionAdvance = Math.max(28, Math.floor(captionAdvance * scale));
                nameAdvance = Math.max(34, Math.floor(nameAdvance * scale));
                lineAdvance = Math.max(28, Math.floor(lineAdvance * scale));
            }

            let sy = bandTop;
            // "SHIP TO" caption (centered)
            zpl += `^FO0,${sy}\n^A0N,${captionFont}\n^FB${labelWidth},1,0,C,0\n^FDSHIP TO^FS\n`;
            sy += captionAdvance;
            // Name / company (emphasized, centered)
            zpl += `^FO0,${sy}\n^A0N,${nameFont}\n^FB${labelWidth},1,0,C,0\n^FD${addressName}^FS\n`;
            sy += nameAdvance;
            // Street / city lines (centered)
            for (const line of addressRest) {
                zpl += `^FO0,${sy}\n^A0N,${lineFont}\n^FB${labelWidth},1,0,C,0\n^FD${line}^FS\n`;
                sy += lineAdvance;
            }
        }

        // Divider above the QR.
        zpl += `^FO${labelX},${qrDividerY}^GB${ruleWidth},3,3^FS\n`;

        // QR code — fixed size, horizontally centered, bottom-anchored.
        const qrBlob = await generateQrBlob(labelInstance.shipmentId);
        const qrZpl = await convertBlobToZPL(qrBlob, 50, qrSize);
        const qrX = Math.round((labelWidth - qrSize) / 2);
        zpl += `^FO${qrX},${qrY}\n${qrZpl}^FS\n`;

        zpl += `^XZ`;
        return zpl;
    }

    if (labelInstance.type === LabelType.C3HardwareItemOrientation) {
        const partNumber = sanitizeForZpl(labelInstance.partNumber);
        const vendorPartNumber = sanitizeForZpl(labelInstance.vendorPartNumber || '');
        const description = sanitizeForZpl(labelInstance.description || 'NO DESCRIPTION');
        const quantity = Math.max(1, Math.floor(labelInstance.printQuantity || 1));

        const landscapeCanvas = createC3HardwareOrientationLabelCanvas(
            partNumber,
            vendorPartNumber,
            description,
            labelInstance.arrowDirection,
        );
        const printerCanvas = rotateCanvasClockwise(landscapeCanvas);
        const labelImageZpl = imageToZPL(printerCanvas).zpl;

        let zpl = `^XA\n^MMT\n^PW812\n^LL1218\n^LS0\n`;
        zpl += `^FO0,0\n${labelImageZpl}^FS\n`;
        zpl += `^PQ${quantity}\n`;
        zpl += `^XZ`;
        return zpl;
    }

    if (labelInstance.type === LabelType.C3HardwareItemBin) {
        const partNumber = sanitizeForZpl(labelInstance.partNumber);
        const description = sanitizeForZpl(
            labelInstance.description || 'NO DESCRIPTION',
        );
        const quantity = Math.max(1, Math.floor(labelInstance.printQuantity || 1));

        // Portrait 4x6 label. The upper portion intentionally stays blank so it
        // can slide under the hardware bin while the identification band remains
        // visible below the bin.
        const labelWidth = 812;
        const labelHeight = 1218;
        const contentTop = 1040;
        const horizontalMargin = 54;
        const contentWidth = labelWidth - (horizontalMargin * 2);

        let zpl = `^XA\n^MMT\n^PW${labelWidth}\n^LL${labelHeight}\n^LS0\n`;
        zpl += `^FO${horizontalMargin},${contentTop}\n`;
        zpl += `^A0N,60\n^FB${contentWidth},1,0,C,0\n^FD${partNumber}^FS\n`;
        zpl += `^FO${horizontalMargin},${contentTop + 72}\n`;
        zpl += `^A0N,30\n^FB${contentWidth},3,0,C,0\n^FD${description}^FS\n`;
        zpl += `^PQ${quantity}\n`;
        zpl += '^XZ';
        return zpl;
    }

    return ''
}

async function generateQrBlob(text: string, width = 400) {
    const dataUrl = await QRCode.toDataURL(text, {
        width,
        margin: 2,
        errorCorrectionLevel: 'H'
    });

    const response = await fetch(dataUrl);
    const blob = await response.blob();

    return blob;
}

interface ZPLResult {
    zpl: string;
    width: number;
    height: number;
    bytes: number;
}

// Utility function to convert Blob to HTMLImageElement
async function blobToImage(blob: Blob): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        if (!(blob instanceof Blob)) {
            reject(new Error("Input must be a Blob"));
            return;
        }
        const url: string = URL.createObjectURL(blob);
        const img: HTMLImageElement = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url); // Clean up
            resolve(img);
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Failed to load image from Blob"));
        };
        img.src = url;
    });
}

// Convert image to monochrome using canvas
function toMonochrome(
    img: HTMLImageElement,
    blacknessThreshold: number = 50,
    targetHeight?: number // ← optional height param
): HTMLCanvasElement {
    if (blacknessThreshold < 0 || blacknessThreshold > 100) {
        throw new Error("Blackness threshold must be between 0 and 100");
    }

    // Maintain aspect ratio
    const scaleFactor = targetHeight ? targetHeight / img.height : 1;
    const width = Math.round(img.width * scaleFactor);
    const height = targetHeight || img.height;

    const canvas: HTMLCanvasElement = document.createElement("canvas");
    const ctx: CanvasRenderingContext2D | null = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
        throw new Error("Canvas 2D context not supported");
    }

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);

    const imageData: ImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data: Uint8ClampedArray = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
        const gray: number = (data[i] + data[i + 1] + data[i + 2]) / 3;
        const value: number = gray < (blacknessThreshold * 255) / 100 ? 0 : 255;
        data[i] = data[i + 1] = data[i + 2] = value;
        data[i + 3] = 255;
    }

    ctx.putImageData(imageData, 0, 0);
    return canvas;
}


function crc16CCITT(data: string): string {
    let crc = 0x0000;
    for (let i = 0; i < data.length; i++) {
        crc ^= data.charCodeAt(i) << 8;
        for (let j = 0; j < 8; j++) {
            crc = (crc & 0x8000) ? (crc << 1) ^ 0x1021 : crc << 1;
            crc &= 0xFFFF;
        }
    }
    return crc.toString(16).padStart(4, '0').toUpperCase();
}

// Convert monochrome canvas to ZPL ^GF command with Z64 compression
function imageToZPL(canvas: HTMLCanvasElement): ZPLResult {
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) throw new Error("Canvas 2D context not supported");

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const width = canvas.width;
    const height = canvas.height;
    const bytesPerRow = Math.ceil(width / 8);
    const totalBytes = bytesPerRow * height;

    // Build raw 1-bit-per-pixel bitmap
    const rawBytes = new Uint8Array(totalBytes);
    for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x += 8) {
            let byte = 0;
            for (let bit = 0; bit < 8 && x + bit < width; bit++) {
                const pixelIndex = (y * width + x + bit) * 4;
                if (data[pixelIndex] < 128) {
                    byte |= 1 << (7 - bit);
                }
            }
            rawBytes[y * bytesPerRow + Math.floor(x / 8)] = byte;
        }
    }

    // Z64: deflate → base64 → CRC
    const compressed = pako.deflate(rawBytes);
    let binaryStr = '';
    for (let i = 0; i < compressed.length; i++) {
        binaryStr += String.fromCharCode(compressed[i]);
    }
    const base64 = btoa(binaryStr);
    const crc = crc16CCITT(base64);
    const z64Data = `:Z64:${base64}:${crc}`;

    const zpl = `^GFA,${z64Data.length},${totalBytes},${bytesPerRow},${z64Data}`;
    return { zpl, width, height, bytes: totalBytes };
}

// Wrap ZPL data with headers/footers
function wrapZPL(zplData: string): string {
    return `${zplData}`;
    // return `^XA^FO0,0${zplData}^FS^XZ`;
}

// Main function to convert Blob to ZPL
async function convertBlobToZPL(blob: Blob, blacknessThreshold = 50, targetHeight?: number): Promise<string> {
    try {
        const img = await blobToImage(blob);
        const canvas = toMonochrome(img, blacknessThreshold, targetHeight);
        const zplResult = imageToZPL(canvas);
        return wrapZPL(zplResult.zpl);
    } catch (error) {
        throw new Error(`Failed to convert Blob to ZPL: ${error instanceof Error ? error.message : String(error)}`);
    }
}

// Example usage
async function example() {
    try {
        const blob: Blob = new Blob([/* image data */], { type: "image/png" });
        const zpl: string = await convertBlobToZPL(blob, 50);
        console.log(zpl);
    } catch (error) {
        console.error(error);
    }
}
